<template>
  <section class="v2-field v2-field--flush" data-qid="task-tests">
    <header class="v2-field__head">
      <h4>{{ $t('testing.title') }}</h4>
      <span class="v2-field__meta">{{ $t('tasks.v2.tests_concluded', { done: concluded, total: tests.length }) }}</span>
      <b-button v-if="canEdit"
                size="sm"
                variant="secondary"
                @click="showTestCreateModal()"
                data-qid="task-tests-new"
      >
        <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('tasks.v2.new_test') }}
      </b-button>
    </header>
    <div class="v2-progress"><span :style="{ width: percent + '%' }"></span></div>

    <table class="v2-tests" v-if="tests.length">
      <thead>
        <tr>
          <th class="v2-tests__no">#</th>
          <th>{{ $t('tasks.v2.test') }}</th>
          <th>{{ $t('tasks.v2.client') }}</th>
          <th>{{ $t('tasks.v2.result') }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(test, index) in tests"
            :key="'test-' + test.id"
            class="v2-tests__row"
            :class="{'v2-tests__row--failed': test.status === 'failed'}"
            @click="openTestModal(test)"
            data-qid="task-test-row"
        >
          <td class="v2-tests__no">{{ index + 1 }}</td>
          <td>
            <div class="v2-tests__name">{{ test.name }}</div>
            <div class="v2-tests__desc" v-if="test.description">{{ test.description }}</div>
          </td>
          <td class="v2-tests__client">{{ clientSummary(test) }}</td>
          <td><span class="v2-tag" :class="statusOf(test).cls">{{ statusOf(test).text }}</span></td>
          <td class="v2-tests__actions" @click.stop>
            <template v-if="canEdit">
              <span class="v2-field__icon"
                    @click="showEditTestModal(test)"
                    v-b-tooltip.hover
                    :title="$t('system.edit')"
                    data-qid="task-test-edit"
              ><font-awesome-icon icon="pen"/></span>
              <span class="v2-field__icon"
                    :class="{'disabled': duplicating}"
                    @click="duplicateTest(test)"
                    v-b-tooltip.hover
                    :title="$t('tasks.v2.duplicate_test')"
                    data-qid="task-test-duplicate"
              ><font-awesome-icon icon="copy"/></span>
              <span class="v2-field__icon v2-field__icon--danger"
                    @click="showTestConfirmDeleteModal(test)"
                    v-b-tooltip.hover
                    :title="$t('system.delete')"
                    data-qid="task-test-delete"
              ><font-awesome-icon icon="trash-alt"/></span>
            </template>
            <font-awesome-icon icon="chevron-right" class="v2-tests__chevron"/>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else-if="loadingTests" class="v2-field__empty"><font-awesome-icon icon="spinner" class="fa-spin"/></div>
    <div v-else class="v2-field__empty">{{ $t('tasks.v2.no_tests') }}</div>
  </section>
</template>

<script>
import assessmentSingleTaskProxy from './assessmentSingleTaskProxy';
import piincHttp from '@/util/configureAxios.js';

// Tests (samples) of the OE testing: one row per test, a click opens the test
export default {
  name: 'EngagementTaskTests',
  mixins: [assessmentSingleTaskProxy],
  data() {
    return {
      duplicating: false
    };
  },
  computed: {
    canEdit() {
      return this.selectedTask && !this.readOnly;
    },
    concluded() {
      return _.filter(this.tests, test => test.status === 'passed' || test.status === 'failed').length;
    },
    percent() {
      return this.tests.length ? Math.round(this.concluded / this.tests.length * 100) : 0;
    }
  },
  methods: {
    // A copy with the same description, settings and files, named "<name>-2" etc. by the server
    duplicateTest(test) {
      if (this.duplicating) {
        return;
      }
      this.duplicating = true;
      piincHttp.post('tasks/' + this.selectedTask.id + '/tests/' + test.id + '/duplicate').then(response => {
        this.duplicating = false;
        this.loadTests();
        this.$toastr('success', this.$t('tasks.v2.test_duplicated', { name: response.data.name }));
      }, error => {
        this.duplicating = false;
        this.$toastr('error', error.data && error.data.message ? error.data.message : '');
      });
    },
    statusOf(test) {
      const statuses = {
        pending: { cls: 'v2-tag--grey', text: this.$t('tasks.v2.test_status.pending') },
        documentation_collection_completed: { cls: 'v2-tag--violet', text: this.$t('tasks.v2.test_status.documentation_collection_completed') },
        passed: { cls: 'v2-tag--teal', text: this.$t('tasks.v2.test_status.passed') },
        failed: { cls: 'v2-tag--red', text: this.$t('tasks.v2.test_status.failed') }
      };
      return statuses[test.status] || statuses.pending;
    },
    clientSummary(test) {
      const files = _.filter(test.media || [], { collection_name: 'documents' }).length;
      let parts = [];
      if (files) {
        parts.push(this.$tc('tasks.v2.files_count', files, { count: files }));
      }
      if (test.description_data) {
        parts.push(this.$t('tasks.v2.answered'));
      }
      return parts.length ? parts.join(' · ') : (test.status === 'pending' ? this.$t('tasks.v2.waiting') : '–');
    }
  }
};
</script>
